import {
  HUBSPOT_TRACKING_COOKIES,
  isHubSpotTrackingConfigured,
  revokeHubSpotTracking,
} from './hubspot.js'

// Vanilla-JS Osano consent layer for the consumer-marketing page.
// Ported from papa-site next-app/lib/osano.js + lib/consent-cleanup.js.
//
//   1. Read the visitor's Osano consent per category (analytics / marketing).
//   2. Notify listeners on init + every consent-save (app.js gates vendor init).
//   3. Delete vendor cookies/localStorage for denied categories, and reload
//      ONCE on a genuine granted->denied withdrawal so in-memory vendor SDKs
//      (Segment, HubSpot, the FB pixel) stop re-writing their cookies.

export const OSANO_CATEGORY = {
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  PERSONALIZATION: 'personalization',
}

// Cookies written by each vendor, keyed by the Osano category that gates it.
// Entries ending in '*' are name prefixes; others are exact names.
const COOKIES_BY_CATEGORY = {
  [OSANO_CATEGORY.ANALYTICS]: [
    'ajs_anonymous_id',
    'ajs_user_id',
    'ajs_group_id', // Segment
    '_ga',
    '_ga_*',
    '_gid',
    '_gat*', // GA4 (gtag.js)
  ],
  [OSANO_CATEGORY.MARKETING]: ['_fbp', '_fbc', '_gcl_*', '_gac_*'], // Meta pixel + Google Ads (gtag.js)
  [OSANO_CATEGORY.PERSONALIZATION]: [],
}

// Segment also persists identity in localStorage, not just cookies.
const LOCALSTORAGE_BY_CATEGORY = {
  [OSANO_CATEGORY.ANALYTICS]: [
    'ajs_anonymous_id',
    'ajs_user_id',
    'ajs_user_traits',
    'ajs_group_id',
    'ajs_group_properties',
  ],
  [OSANO_CATEGORY.MARKETING]: [],
  [OSANO_CATEGORY.PERSONALIZATION]: [],
}

const CATEGORIES = [
  OSANO_CATEGORY.ANALYTICS,
  OSANO_CATEGORY.MARKETING,
  OSANO_CATEGORY.PERSONALIZATION,
]

// Recreate the Osano preload queue defensively (heex head already injects it).
function ensureOsanoQueue() {
  if (typeof window === 'undefined') return
  window.Osano =
    window.Osano ||
    function () {
      ;(window.Osano.data = window.Osano.data || []).push(arguments)
    }
  window.Osano.data = window.Osano.data || []
}

// Synchronous consent read. False until Osano initializes (safe default).
export function hasConsent(category) {
  if (typeof window === 'undefined') return false
  const cm = window.Osano && window.Osano.cm
  return !!(cm && cm[category])
}

function readConsent() {
  const out = {}
  CATEGORIES.forEach(c => {
    out[c] = hasConsent(c)
  })
  return out
}

// Register a listener that fires on Osano init and on every consent-save.
// Uses the CMP API when osano.js is loaded, else the queue API. Returns an
// unsubscribe fn.
export function onConsentChange(cb) {
  ensureOsanoQueue()
  const handler = () => cb(readConsent())

  const cm = window.Osano && window.Osano.cm
  if (cm && cm.addEventListener) {
    cm.addEventListener('osano-cm-initialized', handler)
    cm.addEventListener('osano-cm-consent-saved', handler)
    handler() // may already be initialized -> fire once now
    return () => {
      const api = window.Osano && window.Osano.cm
      if (api && api.removeEventListener) {
        api.removeEventListener('osano-cm-initialized', handler)
        api.removeEventListener('osano-cm-consent-saved', handler)
      }
    }
  }

  // Queue path: onInitialized fires immediately if already initialized. No
  // symmetric queue removal, so guard with `active`.
  let active = true
  const guarded = () => {
    if (active) handler()
  }
  window.Osano('onInitialized', guarded)
  window.Osano('onConsentSaved', guarded)
  return () => {
    active = false
  }
}

// --- cookie / localStorage cleanup -----------------------------------------

const HUBSPOT_SHARED_COOKIE_DOMAINS = ['papa.com']

function hubspotSharedCookieDomain(host) {
  return HUBSPOT_SHARED_COOKIE_DOMAINS.find(
    domain => host === domain || host.endsWith(`.${domain}`),
  )
}

// DECISION: non-HubSpot vendors remain host/subdomain scoped so withdrawing
// consent here does not clear cookies shared with sibling Papa properties.
// CAVEAT: Segment (ajs_*) and Meta (_fbp/_fbc) often use the registrable domain,
// so this will not remove those shared cookies. Reconcile that cross-property
// policy before broadening their cleanup.
//
// HubSpot is the approved exception while its feature is enabled: portal
// 4038551 shares visitor identity across Papa properties, so clear its exact
// cookie names on papa.com. Non-production and unknown hosts stay host-only.
function cookieDomains(name) {
  const host = window.location.hostname
  const domains = new Set([host, `.${host}`])

  if (isHubSpotTrackingConfigured() && HUBSPOT_TRACKING_COOKIES.includes(name)) {
    const sharedDomain = hubspotSharedCookieDomain(host)
    if (sharedDomain) {
      domains.add(sharedDomain)
      domains.add(`.${sharedDomain}`)
    }
  }

  return domains
}

function expireCookie(name) {
  const expired = 'Thu, 01 Jan 1970 00:00:00 GMT'
  document.cookie = `${name}=; path=/; expires=${expired}; SameSite=Lax`
  for (const domain of cookieDomains(name)) {
    document.cookie = `${name}=; path=/; domain=${domain}; expires=${expired}; SameSite=Lax`
  }
}

// Resolve '*' prefixes against the live cookie jar to concrete names.
function cookiePatternsForCategory(category) {
  const patterns = [...(COOKIES_BY_CATEGORY[category] || [])]

  if (category === OSANO_CATEGORY.MARKETING && isHubSpotTrackingConfigured()) {
    patterns.push(...HUBSPOT_TRACKING_COOKIES)
  }

  return patterns
}

function cookieNamesToDelete(category) {
  const patterns = cookiePatternsForCategory(category)
  const present = document.cookie
    .split(';')
    .map(c => c.split('=')[0].trim())
    .filter(Boolean)
  const names = new Set()
  patterns.forEach(p => {
    if (p.endsWith('*')) {
      const prefix = p.slice(0, -1)
      present.forEach(n => {
        if (n.startsWith(prefix)) names.add(n)
      })
    } else {
      names.add(p) // delete by exact name even if not currently visible
    }
  })
  return names
}

function clearCategory(category) {
  if (category === OSANO_CATEGORY.MARKETING) revokeHubSpotTracking()
  cookieNamesToDelete(category).forEach(expireCookie)
  try {
    ;(LOCALSTORAGE_BY_CATEGORY[category] || []).forEach(key => window.localStorage.removeItem(key))
  } catch {
    // localStorage may be unavailable (private mode / blocked); ignore.
  }
}

let registered = false

// Deletes vendor cookies/localStorage when a category is withdrawn, and reloads
// ONCE on a genuine withdrawal so consent-gated init in app.js doesn't re-run.
// Idempotent.
export function registerConsentCookieCleanup() {
  if (typeof window === 'undefined' || registered) return
  registered = true
  ensureOsanoQueue()

  let previous = null // baseline of granted categories for this page session

  const handle = () => {
    const current = readConsent()

    // Sweep cookies for every currently-denied category (also cleans stragglers
    // from a prior session on first run).
    CATEGORIES.forEach(c => {
      if (!current[c]) clearCategory(c)
    })

    if (previous === null) {
      // first invocation: record baseline, no reload
      previous = current
      return
    }

    // Reload only when a category that actually writes cookies flipped
    // granted -> denied (a real withdrawal).
    const revoked = CATEGORIES.some(
      c => previous[c] && !current[c] && cookiePatternsForCategory(c).length > 0,
    )
    previous = current
    if (revoked) window.location.reload()
  }

  const cm = window.Osano && window.Osano.cm
  if (cm && cm.addEventListener) {
    cm.addEventListener('osano-cm-initialized', handle)
    cm.addEventListener('osano-cm-consent-saved', handle)
    handle() // cm exists -> consent is readable; safe to run baseline sweep now
  } else {
    // cm not ready: onInitialized runs the sweep once Osano finishes loading.
    window.Osano('onInitialized', handle)
    window.Osano('onConsentSaved', handle)
  }
}
